import {
  CallbackProperty,
  Color,
  ColorMaterialProperty,
  Transforms,
  HeadingPitchRoll,
  createGuid,
  Camera,
  ShadowMap,
  Cartesian2,
  Cartesian3,
  Cartesian4,
  PostProcessStage,
  Entity,
  Math as CesiumMath,
} from "@cesium/engine";
//import PopupAnalysisVisibility from "../popup/PopupAnalysisVisibility";
import DitapViewer from "../basic/DitapViewer";

/**
 * 가시성 분석 쉐이더.</br>
 * Visibility analysis shader
 * @private
 */
const VISIBILITY_SHADER =
  "#version 300 es" +
  "\nout vec4 FragColor;" +
  "\nuniform sampler2D colorTexture;" + //# 색상 텍스처
  "\nuniform sampler2D depthTexture;" + //# depth 텍스처
  "\nin vec2 v_textureCoordinates;" + //# 텍스처 매핑을 위한 텍스처 좌표
  "\nuniform mat4 camera_projection_matrix;" + //# 카메라의 절두체 투영 matrix
  "\nuniform mat4 camera_view_matrix;" + //# 카메라 보는 방향 matrix
  "\nuniform samplerCube shadowMap_textureCube;" + //# shadowmap cube(6면체) 텍스처
  "\nuniform mat4 shadowMap_matrix;" + //# shadowmap matrix
  "\nuniform vec4 shadowMap_lightPositionEC;" + //
  "\nuniform vec4 shadowMap_normalOffsetScaleDistanceMaxDistanceAndDarkness;" +
  "\nuniform vec4 shadowMap_texelSizeDepthBiasAndNormalShadingSmooth;" +
  "\nuniform float helsing_viewDistance; " + //# depth 적용 할 최대 길이
  "\nuniform vec4 helsing_visibleAreaColor;" + //# backface color
  "\nuniform vec4 helsing_invisibleAreaColor;" + //# frontface color
  "\nstruct zx_shadowParameters" +
  "\n{" +
  "\n vec3 texCoords;" +
  "\n float depthBias;" +
  "\n float depth;" +
  "\n float nDotL;" +
  "\n vec2 texelStepSize;" +
  "\n float normalShadingSmooth;" +
  "\n float darkness;" +
  "\n};" +
  "\nfloat czm_shadowVisibility(samplerCube shadowMap, zx_shadowParameters shadowParameters)" +
  "\n{" +
  "\n float depthBias = shadowParameters.depthBias;" +
  "\n float depth = shadowParameters.depth;" +
  "\n float nDotL = shadowParameters.nDotL;" +
  "\n float normalShadingSmooth = shadowParameters.normalShadingSmooth;" +
  "\n float darkness = shadowParameters.darkness;" +
  "\n vec3 uvw = shadowParameters.texCoords;" +
  "\n depth -= depthBias;" +
  "\n float visibility = czm_shadowDepthCompare(shadowMap, uvw, depth);" +
  "\n return czm_private_shadowVisibility(visibility, nDotL, normalShadingSmooth, darkness);" +
  "\n}" +
  "\nvec3 getNormalEC(){" +
  "\n return vec3(1.);" +
  "\n}" +
  "\nvec4 toEye(in vec2 textureCoordinates, in float depth){" +
  "\n vec2 xy = textureCoordinates * 2.0 - 1.0;" +
  "\n vec4 viewPos = czm_inverseProjection * vec4(xy, depth, 1.0);" +
  "\n viewPos = viewPos / viewPos.w;" +
  "\n return viewPos;" +
  "\n}" +
  "\nfloat getDepth(in vec4 depth){" +
  "\n float z_window=czm_unpackDepth(depth);" + //# 0~1 사이의 depth 값
  "\n z_window=czm_reverseLogDepth(z_window);" + //#
  "\n float n_range=czm_depthRange.near;" +
  "\n float f_range=czm_depthRange.far;" +
  "\n return(2.*z_window-n_range-f_range)/(f_range-n_range);" + //# f_range > n_range 일때 z_window가 크면 멀리 있고 작으면 가까이 있음
  "\n}" +
  "\nfloat shadow(in vec4 positionEC){" +
  "\n	vec3 normalEC=getNormalEC();" +
  "\n	zx_shadowParameters shadowParameters;" +
  "\n	shadowParameters.texelStepSize=shadowMap_texelSizeDepthBiasAndNormalShadingSmooth.xy;" +
  "\n	shadowParameters.depthBias=shadowMap_texelSizeDepthBiasAndNormalShadingSmooth.z;" +
  "\n	shadowParameters.normalShadingSmooth=shadowMap_texelSizeDepthBiasAndNormalShadingSmooth.w;" +
  "\n	shadowParameters.darkness=shadowMap_normalOffsetScaleDistanceMaxDistanceAndDarkness.w;" +
  "\n	vec3 directionEC=positionEC.xyz-shadowMap_lightPositionEC.xyz;" +
  "\n	float distance=length(directionEC);" +
  "\n	directionEC=normalize(directionEC);" +
  "\n	float radius=shadowMap_lightPositionEC.w;" +
  "\n	if(distance>radius)" +
  "\n	{" +
  "\n		return 2.0;" +
  "\n	}" +
  "\n	vec3 directionWC=inverse(mat3(czm_view)) * directionEC;" +
  "\n	shadowParameters.depth=distance/radius-0.0003;" +
  "\n	shadowParameters.nDotL=clamp(dot(normalEC,-directionEC),0.,1.);" +
  "\n	shadowParameters.texCoords=directionWC;" +
  "\n	float visibility=czm_shadowVisibility(shadowMap_textureCube,shadowParameters);" +
  "\n	return visibility;" +
  "\n}" +
  "\nbool visible(in vec4 result)" +
  "\n{" +
  "\n	result.xyz /= result.w;" +
  "\n	return all(greaterThanEqual(result.xyz, vec3(-1.))) && all(lessThanEqual(result.xyz, vec3(1.)));" +
  "\n}" +
  "\nvoid main(){" +
  "\n	FragColor = texture(colorTexture, v_textureCoordinates);" + //# 좌표에 있는 텍스처값 반환
  "\n	float depth = getDepth(texture(depthTexture, v_textureCoordinates));" + //# 2D에서 depth값
  "\n	vec4 viewPos = toEye(v_textureCoordinates, depth);" + //# 눈좌표 Depth
  "\n	vec4 wordPos = czm_inverseView * viewPos;" + //# 눈좌표 depth -> world 좌표
  "\n	vec4 vcPos = camera_view_matrix * wordPos;" + //# 카메라 view 방향으로 전환
  "\n	float near = .001 * helsing_viewDistance;" + //# 원점 ~ 그림자 시작위치 길이
  "\n	float dis = length(vcPos.xyz);" + //# 원점 ~ 그림자 종료위치 길이
  "\n	if(dis > near && dis < helsing_viewDistance){" +
  "\n		vec4 posInEye = camera_projection_matrix * vcPos;" + //# 카메라 matrix
  "\n		if(visible(posInEye)){" + //# 현재 시야에 있는지?
  "\n			float vis = shadow(viewPos);" + //# 3D 상태
  "\n			if(vis > 0.3){" +
  "\n				FragColor = mix(FragColor,helsing_visibleAreaColor,.4);" +
  "\n			} else{" +
  "\n				FragColor = mix(FragColor,helsing_invisibleAreaColor,.4);" +
  "\n			}" +
  "\n		}" +
  "\n	}" +
  "\n}";

/**
 * 가시성 분석 객체 생성 옵션.</br>
 * Visibility analysis object creation options
 * @private
 */
const VISIBILITY_OPTIONS = {
  ASPECTRATIO: 1.5,
  FOV: 90,
  DISTANCE: 150,
  HEADING: 0,
  PITCH: 180,
  ROLL: 0,
  POSITION: undefined,
  CALLBACK: undefined,
};

/**
 * 가시성 분석 연산 및 영역 표시 클래스. </br>
 * Visibility analysis operation and area display class.
 * @alias AnalysisVisibility
 *
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {object} props 각각 다음과 같은 속성을 가진 객체.<br> Object with the following properties:
 * @param {Cartesian3} [props.position] 가시성 분석 위치.</br> Position.
 * @param {number} [props.aspectRatio=1.5] 가시성 분석 영역 종횡비 (가로/세로).</br> AspectRatio (width/height).
 * @param {number} [props.fov=90] 가시성 분석 시야 각도 (도).</br> Fov (degrees).
 * @param {number} [props.distance=150] 가시성 분석 거리 (미터).</br> Distance (meters).
 * @param {number} [props.heading=0] 수직축 회전 각도 (도).</br> Heading (degrees).
 * @param {number} [props.pitch=180] 횡축 회전 각도 (도).</br> Pitch (degrees).
 * @param {number} [props.roll=0] 종축 회전 각도 (도).</br> Roll (degrees).
 * @param {function} [props.callback=undefined] 가시성 분석 엔티티 생성후 콜백 함수.</br> Callback function after creation of visibility analytics entity.
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * // 1. 마우스를 이용한 위치 지정 후 객체 생성
 * const analysisVisibility = new Ditap.AnalysisVisibility(viewer);
 * const interactionVisibility = new Ditap.InteractionVisibility(viewer);
 * interactionVisibility.analysisVisibility = analysisVisibility;
 * interactionVisibility.on();
 *
 * // 2. 위치 지정하여 생성
 * const position = new Ditap.Cartesian3(-3044811.6264139246, 4043796.6433953536, 3867466.40813661);
 * const props = {
 *   position: position,  // 위치*
 *   // aspectRatio: 1.5, // 종횡비
 *   // fov: 90,          // 화각
 *   // distance: 150,    // 가시거리
 *   // heading: 0,       // 헤딩
 *   // pitch: 180,       // 피치
 *   // roll: 0,          // 롤
 *   // callback: ()=>{}  // 콜백함수
 * };
 * const analysisVisibility = new Ditap.AnalysisVisibility(viewer, props);
 *
 */
class AnalysisVisibility {
  constructor(viewer, props) {
    // id
    this._id = `AnalysisVisibility${createGuid()}`;

    this._viewer = undefined;
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      this._viewer.ditapStorage.analysisVisibility[this._id] = this;
    } else {
      throw Error("AnalysisVisibility viewer type error");
    }

    // 가시성 분석 생성 객체
    this._frustum = undefined;
    this._lightCamera = undefined;
    this._shadowMap = undefined;
    this._postProcessStages = undefined;

    this._aspectRatio = 1.5;
    this._fov = 90;
    this._heading = 0;
    this._pitch = 180;
    this._roll = 0;
    this._distance = 150;
    this._position = undefined;
    this._callback = undefined;

    if (props instanceof Object) {
      this.createVisibility(props);
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스. </br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  get viewer() {
    return this._viewer;
  }
  set viewer(viewer) {
    this._viewer = viewer;
  }

  /**
   * 가시성 분석 프러스텀. </br>
   * Visibility analysis frustum.
   * @type {Entity}
   * @readonly
   */
  get frustum() {
    return this._frustum;
  }

  /**
   * 그림자맵 빛 카메라. </br>
   * Light camera in shadow map.
   * @type {Camera}
   * @readonly
   */
  get lightCamera() {
    return this._lightCamera;
  }

  /**
   * 그림자맵. </br>
   * Visibility analysis shadow map.
   * @type {ShadowMap}
   * @readonly
   */
  get shadowMap() {
    return this._shadowMap;
  }

  /*
   * 후처리 객체. </br>
   * Visibility analysis post-process stage.
   * @type {PostProcessStage}
   */
  get postProcessStages() {
    return this._postProcessStages;
  }
  set postProcessStages(postProcessStages) {
    this._postProcessStages = postProcessStages;
  }

  /**
   * 가시성 분석 객체 ID. </br>
   * AnalysisVisibility instance id.
   * @type {string}
   * @readonly
   */
  get id() {
    return this._id;
  }

  /**
   * 가시성 분석 영역 종횡비. </br>
   * Visibility analysis light camera aspectRatio.
   * @type {number}
   * @readonly
   */
  get aspectRatio() {
    return this._aspectRatio;
  }

  /**
   * 가시성 분석 시야 각도. </br>
   * Visibility analysis light camera fov.
   * @type {number}
   * @readonly
   */
  get fov() {
    return this._fov;
  }

  /**
   * 가시성 분석 거리.  </br>
   * Visibility analysis light camera distance.
   * @type {number}
   * @readonly
   */
  get distance() {
    return this._distance;
  }

  /**
   * 수직축 회전 각도. </br>
   * Visibility analysis light camera heading.
   * @type {number}
   * @readonly
   */
  get heading() {
    return this._heading;
  }

  /**
   * 횡축 회전 각도. </br>
   * Visibility analysis light camera pitch.
   * @type {number}
   * @readonly
   */
  get pitch() {
    return this._pitch;
  }

  /**
   * 종축 방향 회전 각도. </br>
   * Visibility analysis light camera roll.
   * @type {number}
   * @readonly
   */
  get roll() {
    return this._roll;
  }

  /**
   * 가시성 분석 위치. </br>
   * Visibility analysis light camera position.
   * @type {Cartesian3}
   * @readonly
   */
  get position() {
    return this._position;
  }

  /**
   * 활성화 여부 </br>
   * Activation Status.
   * @type {boolean}
   * @readonly
   */
  get isReady() {
    return this._isReady;
  }

  /**
   * 가시성 분석 엔티티 생성후 콜백 함수.</br>
   * Callback function after creation of visibility analytics entity.
   * @type {Function | undefined}
   * @readonly
   */
  get callback() {
    return this._callback;
  }

  /**
   * 가시성 분석 빛 카메라 생성
   * Create shadow map light camera
   * @private
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스. </br> The DitapViewer instance to use.
   * @param {Cartesian3} position 카메라 좌표. </br> Camera position
   * @param {number} [props.aspectRatio=1.5] 가시성 분석 영역 종횡비 (가로/세로).</br> AspectRatio (Width/Height).
   * @param {number} [props.fov=90] 가시성 분석 시야 각도 (도).</br> Fov (degrees).
   * @param {number} [props.distance=150] 가시성 분석 거리 (미터).</br> Distance (degrees).
   * @param {number} [props.heading=0] 수직축 회전 각도 (도).</br> Heading (degrees).
   * @param {number} [props.pitch=180] 횡축 회전 각도 (도).</br> Pitch (degrees).
   * @param {number} [props.roll=0] 종축 회전 각도 (도).</br> Roll (degrees).
   * @returns {Camera} 카메라 인스턴스. </br> Camera instance
   */
  createCamera(
    viewer,
    position,
    distance = 100,
    heading = 0,
    pitch = 0,
    roll = 0,
    fov = 90,
    aspectRatio = 1.5
  ) {
    const camera = new Camera(viewer.scene);
    camera.position = position;
    camera.frustum.near = distance * 0.001;
    camera.frustum.far = distance;
    camera.frustum.fov = fov;
    camera.frustum.aspectRatio = aspectRatio;

    camera.setView({
      destination: position,
      orientation: {
        heading: CesiumMath.toRadians(heading),
        pitch: CesiumMath.toRadians(pitch),
        roll: CesiumMath.toRadians(roll),
      },
    });

    return camera;
  }

  /**
   * 카메라 수직축 회전 각도 설정. </br>
   * Set light camera heading.
   * @param {number} value 각도 (도).</br> Heading (degrees).
   */
  setHeading(value) {
    if (!(-180 <= value && value <= 180)) {
      throw Error("AnalysisVisibility -180 <= heading <= 180");
    }

    this._heading = value;
    this._lightCamera = this.createCamera(
      this.viewer,
      this._position,
      this._distance,
      value,
      this._pitch,
      this._roll,
      CesiumMath.toRadians(this._fov),
      this._aspectRatio
    );
  }

  /**
   * 카메라 횡축 회전 각도 설정. </br>
   * Set light camera pitch.
   * @param {number} value 각도 (도).</br> Pitch (degrees).
   */
  setPitch(value) {
    if (!(-180 <= value && value <= 180)) {
      throw Error("AnalysisVisibility -180 <= pitch <= 180");
    }

    this._pitch = value;
    this._lightCamera = this.createCamera(
      this.viewer,
      this._position,
      this._distance,
      this._heading,
      value,
      this._roll,
      CesiumMath.toRadians(this._fov),
      this._aspectRatio
    );
  }

  /**
   * 카메라 종축 방향 회전 각도 설정. </br>
   * Set light camera roll.
   * @param {number} value 각도 (도).</br> Roll (degrees).
   */
  setRoll(value) {
    if (!(-180 <= value && value <= 180)) {
      throw Error("AnalysisVisibility -180 <= roll <= 180");
    }

    this._roll = value;
    this._lightCamera = this.createCamera(
      this.viewer,
      this._position,
      this._distance,
      this._heading,
      this._pitch,
      value,
      CesiumMath.toRadians(this._fov),
      this._aspectRatio
    );
  }

  /**
   * 카메라 종횡비 설정. </br>
   * Set light camera aspectRatio.
   * @param {number} value 종횡비 (가로/세로).</br> AspectRatio (width/height).
   */
  setAspectRatio(value) {
    if (value < 0) {
      throw Error("AnalysisVisibility 0 < aspectRatio");
    }

    this._aspectRatio = value;
    this._lightCamera = this.createCamera(
      this.viewer,
      this._position,
      this._distance,
      this._heading,
      this._pitch,
      this._roll,
      CesiumMath.toRadians(this._fov),
      value
    );
  }

  /**
   * 카메라 시야 각도 설정. </br>
   * Set light camera fov.
   * @param {number} value 각도 (도).</br> Fov (degrees).
   */
  setFov(value) {
    if (!(0 < value && value < 180)) {
      throw Error("AnalysisVisibility 0 < fov < 180");
    }

    this._fov = value;
    this._lightCamera = this.createCamera(
      this.viewer,
      this._position,
      this._distance,
      this._heading,
      this._pitch,
      this._roll,
      CesiumMath.toRadians(this._fov),
      this._aspectRatio
    );
  }

  /**
   * 카메라 거리 설정. </br>
   * Set light camera distance.
   * @param {number} value 거리 (미터).</br> Distance (meters).
   */
  setDistance(value) {
    if (value < 1) {
      throw Error("AnalysisVisibility 1 <= distance");
    }

    this._distance = value;
    this._lightCamera = this.createCamera(
      this.viewer,
      this._position,
      value,
      this._heading,
      this._pitch,
      this._roll,
      CesiumMath.toRadians(this._fov),
      this._aspectRatio
    );

    this._shadowMap._pointLightRadius = this._distance;
  }

  /**
   * 가시성 분석 객체 생성. </br>
   * Create visibility analysis object.
   * @param {object} props 각각 다음과 같은 속성을 가진 객체. </br> Object with the following properties:
   * @param {Cartesian3} props.position 가시성 분석 위치. </br> Position.
   * @param {number} [props.aspectRatio=1.5] 가시성 분석 영역 종횡비 (가로/세로).</br> AspectRatio (width/height).
   * @param {number} [props.fov=90] 가시성 분석 시야 각도 (도).</br> Fov (degrees).
   * @param {number} [props.distance=150] 가시성 분석 거리 (미터).</br> Distance (meters).
   * @param {number} [props.heading=0] 수직축 회전 각도 (도).</br> Heading (degrees).
   * @param {number} [props.pitch=180] 횡축 회전 각도 (도).</br> Pitch (degrees).
   * @param {number} [props.roll=0] 종축 회전 각도 (도).</br> Roll (degrees).ters)
   * @param {function} [props.callback=undefined] 생성 후 콜백 함수. </br> Callback function after creation of visibility analytics entity.
   */
  createVisibility(props = {}) {
    this.clear();

    // props.aspectRatio
    this._aspectRatio = !isNaN(props.aspectRatio)
      ? props.aspectRatio
      : VISIBILITY_OPTIONS.ASPECTRATIO;
    if (this._aspectRatio < 0) {
      throw Error("AnalysisVisibility 0 < props.aspectRatio");
    }

    // props.fov
    this._fov = !isNaN(props.fov) ? props.fov : VISIBILITY_OPTIONS.FOV;
    if (!(0 < this._fov && this._fov < 180)) {
      throw Error("AnalysisVisibility 0 < props.fov < 180");
    }

    // props.heading
    this._heading = !isNaN(props.heading)
      ? props.heading
      : VISIBILITY_OPTIONS.HEADING;
    if (!(-180 <= this._heading && this._heading <= 180)) {
      throw Error("AnalysisVisibility -180 <= props.heading <= 180");
    }

    // props.pitch
    this._pitch = !isNaN(props.pitch) ? props.pitch : VISIBILITY_OPTIONS.PITCH;
    if (!(-180 <= this._pitch && this._pitch <= 180)) {
      throw Error("AnalysisVisibility -180 <= props.pitch <= 180");
    }

    // props.roll
    this._roll = !isNaN(props.roll) ? props.roll : VISIBILITY_OPTIONS.ROLL;
    if (!(-180 <= this._roll && this._roll <= 180)) {
      throw Error("AnalysisVisibility -180 <= props.roll <= 180");
    }

    // props.distance
    this._distance = !isNaN(props.distance)
      ? props.distance
      : VISIBILITY_OPTIONS.DISTANCE;
    if (!(1 <= this._distance)) {
      throw Error("AnalysisVisibility 1 <= props.distance");
    }

    // props.callback
    this._callback = props.callback || VISIBILITY_OPTIONS.CALLBACK;
    if (!(this._callback instanceof Function || this._callback === undefined)) {
      throw Error("AnalysisVisibility props.callback type error");
    }

    // props.position
    this._position = props.position || VISIBILITY_OPTIONS.POSITION;
    if (!(this._position instanceof Cartesian3)) {
      throw Error("AnalysisVisibility props.position type error");
    }

    const viewer = this.viewer;
    const id = this._id;

    // 1. frustum 생성 => 가시성 분석 영역을 눈으로 표시하기 위한 엔티티일뿐 실제로 가시성 분석을 하지는 않음
    const frustumInfo = {
      id: `${id}_frustum`,
      position: new CallbackProperty(() => {
        // ellipsoid 중심점
        return this._position;
      }, false),
      orientation: new CallbackProperty(() => {
        // ellipsoid 최대 영역
        return Transforms.headingPitchRollQuaternion(
          this._position,
          HeadingPitchRoll.fromDegrees(
            this._heading - 90,
            this._pitch,
            this._roll
          )
        );
      }, false),
      ellipsoid: {
        radii: new CallbackProperty(() => {
          // 구 크기
          return new Cartesian3(this._distance, this._distance, this._distance);
        }, false),
        // innerRadii: new Ditap.CallbackProperty(()=>{
        // 	return new Ditap.Cartesian3(
        // 		this.visibility_entity_info[id].transform.distance,
        // 		this.visibility_entity_info[id].transform.distance,
        // 		this.visibility_entity_info[id].transform.distance
        // 	)
        // }, false),
        innerRadii: new Cartesian3( // ellipsoid 안쪽에 비우는 영역 == lightCamera.frustum.far 영역
          this._distance * 0.001,
          this._distance * 0.001,
          this._distance * 0.001
        ),
        // -90 ~ 0
        minimumClock: new CallbackProperty(() => {
          // 원의 가로길이(최소)
          let value = -this._fov / 2;

          if (this._aspectRatio < 1) {
            value = -CesiumMath.toDegrees(
              (this._lightCamera.frustum.aspectRatio *
                this._lightCamera.frustum.fovy) /
                2
            );
          }

          if (value < -90) {
            value = -90;
          } else if (0 < value) {
            value = 0;
          }

          return CesiumMath.toRadians(value);
        }, false),
        // 0 ~ 90
        maximumClock: new CallbackProperty(() => {
          // 원의 가로길이(최대)
          let value = this._fov / 2;

          if (this._aspectRatio < 1) {
            value = CesiumMath.toDegrees(
              (this._lightCamera.frustum.aspectRatio *
                this._lightCamera.frustum.fovy) /
                2
            );
          }

          if (value < 0) {
            value = 0;
          } else if (90 < value) {
            value = 90;
          }

          return CesiumMath.toRadians(value);
        }, false),
        // 0 ~ 90
        minimumCone: new CallbackProperty(() => {
          // 세로길이 시작 각도
          // return Ditap.Math.toRadians(90-Math.tan(this.visibility_entity_info[id].transform.vertical/this.visibility_entity_info[id].transform.distance) * 180 / Math.PI);
          let value = 90 - this._fov / 2 / this._aspectRatio;

          if (this._aspectRatio < 1) {
            value = 90 - this._fov / 2;
          }

          if (value < 0) {
            value = 0;
          } else if (90 < value) {
            value = 90;
          }

          return CesiumMath.toRadians(value);
        }, false),
        // 90 ~ 180
        maximumCone: new CallbackProperty(() => {
          // 세로길이 종료 각도
          //return Ditap.Math.toRadians(90+Math.tan(this.visibility_entity_info[id].transform.vertical/this.visibility_entity_info[id].transform.distance) * 180 / Math.PI);
          let value = 90 + this._fov / 2 / this._aspectRatio;

          if (this._aspectRatio < 1) {
            value = 90 + this._fov / 2;
          }

          if (value < 90) {
            value = 90;
          } else if (180 < value) {
            value = 180;
          }

          return CesiumMath.toRadians(value);
        }, false),
        fill: true, //# ellipsoid plane ON
        //material : new Ditap.ColorMaterialProperty(Ditap.Color.WHITE.withAlpha(0.4)), //# ellipsoid plane color
        material: new ColorMaterialProperty(
          new CallbackProperty(() => {
            // if (this.hover == id) {
            //   return Color.YELLOW.withAlpha(0.6);
            // }
            return Color.WHITE.withAlpha(0.4);
          }, false)
        ),
        // subdivisions: 256, //# outline number of point
        outline: true, //# outline ON
        outlineColor: Color.YELLOWGREEN, //# outline 색상
        stackPartitions: 32, //# 그물망 세로 갯수
        slicePartitions: 32, //# 그물망 가로 갯수
      },
    };
    this._frustum = viewer.entities.add(frustumInfo);

    // 2. lightCamera 생성 => 가시성 분석 영역을 지정하는 Camera 엔티티
    const lightCamera = this.createCamera(
      viewer,
      this._position,
      this._distance,
      this._heading,
      this._pitch,
      this._roll,
      CesiumMath.toRadians(this._fov),
      this._aspectRatio
    );
    this._lightCamera = lightCamera;

    // 3. shadowMap 생성 => postProcessStages에서 가시성 분석 결과를 lightCamera 영역에 표출하는 역할
    const shadowMap = new ShadowMap({
      context: viewer.scene.context,
      lightCamera: this._lightCamera,
      enabled: true,
      isPointLight: true,
      pointLightRadius: this._distance,
      cascadesEnabled: false,
      size: 4096,
      softShadows: true,
      normalOffset: false,
      fromLightSource: false,
    });
    this._shadowMap = shadowMap;
    viewer.scene.shadowMap = this._shadowMap;

    // this.frustum = viewer.scene.primitives.add(new DebugCameraPrimitive({
    //   camera : this.lightCamera,
    //   color : Color.YELLOWGREEN
    // }));

    // 4. postProcessStages 생성 => 가시성 분석을 통해 depthFail 영역을 컬러로 지정해주는 역할
    const post1 = new PostProcessStage({
      fragmentShader: (() => {
        return VISIBILITY_SHADER;
      })(),
      uniforms: {
        shadowMap_textureCube: () => {
          return this._shadowMap._shadowMapTexture; //# 그림자 텍스처
        },
        shadowMap_matrix: () => {
          return this._shadowMap._shadowMapMatrix; //# 그림자 방향 matrix
        },
        shadowMap_lightPositionEC: () => {
          return this._shadowMap._lightPositionEC; //#
        },
        shadowMap_normalOffsetScaleDistanceMaxDistanceAndDarkness: () => {
          this._shadowMap.update(viewer.scene._frameState);
          return Cartesian4.fromElements(
            this._shadowMap._pointBias.normalOffsetScale,
            this._shadowMap._distance,
            this._shadowMap.maximumDistance,
            this._shadowMap._darkness,
            new Cartesian4()
          );
        },
        shadowMap_texelSizeDepthBiasAndNormalShadingSmooth: () => {
          this._shadowMap.update(viewer.scene._frameState);
          const bias = this._shadowMap._pointBias;
          const scratchTexelStepSize = new Cartesian2();
          const texelStepSize = scratchTexelStepSize;
          texelStepSize.x = 1.0 / shadowMap._textureSize.x;

          return Cartesian4.fromElements(
            texelStepSize.x,
            texelStepSize.y,
            bias.depthBias,
            bias.normalShadingSmooth,
            new Cartesian4()
          );
        },
        //camera_projection_matrix: lightCamera.frustum.projectionMatrix,
        camera_projection_matrix: () => {
          return this._lightCamera.frustum.projectionMatrix; //# horizon, vertical
        },
        //camera_view_matrix: lightCamera.viewMatrix,
        camera_view_matrix: () => {
          return this._lightCamera.viewMatrix; //# heading, pitch, roll
        },
        helsing_viewDistance: () => {
          return this._distance; //# 가시권 영역 최대거리(최소거리 = 최대거리 * 0.001)
        },
        helsing_visibleAreaColor: Color.GREEN, //# 가시권 영역 색상
        helsing_invisibleAreaColor: Color.RED, //# 비가시권 영역 색상
      },
    });

    viewer.scene.postProcessStages.add(post1);
    this.postProcessStages = post1;

    this._isReady = true;

    if (this._callback instanceof Function) {
      this._callback(this);
    }

    //this.openPopup();
    //viewer.ditapStorage.analysisVisibility[this._id] = this;
  }

  /**
   * 모든 엔티티 제거. </br>
   * Remove all created entities
   */
  clear() {
    const viewer = this.viewer;

    viewer.scene.shadowMap = undefined;
    if (this._frustum instanceof Entity) {
      viewer.entities.remove(this._frustum);
    }
    if (this.postProcessStages instanceof PostProcessStage) {
      viewer.scene.postProcessStages.remove(this.postProcessStages);
    }

    this._frustum = undefined;
    this.postProcessStages = undefined;
    this._lightCamera = undefined;
    this._shadowMap = undefined;
    this._isReady = false;
  }

  /**
   * 인스턴스 제거. </br>
   * Destroys the instance.
   */
  destroy() {
    // Entity 제거
    this.clear();

    // viewerStorage에서 삭제
    this.viewer.ditapStorage.removeInstanceById(this._id);

    //this.popupAnalysisVisibility.destroy();
  }
}

export default AnalysisVisibility;
